import React from 'react';
import { X } from 'lucide-react';

type ConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
};

function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirmação", // Default title
  message,
  confirmText = "Confirmar", // Default confirm text
  cancelText = "Cancelar", // Default cancel text
}: ConfirmationModalProps) {
  // Prevent background scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } 
    // Cleanup function: always run when isOpen changes OR component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]); // Depend on isOpen

  // Return null if not open AFTER the effect hook
  if (!isOpen) return null;

  return (
    // Backdrop
    <div 
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4 transition-opacity duration-300 ease-in-out"
      onClick={onClose} // Close on backdrop click
    >
      {/* Modal Content - Stop propagation to prevent backdrop click */} 
      <div
        className="bg-salus-branco w-full max-w-md rounded-xl shadow-2xl p-6 mx-auto relative flex flex-col gap-4 animate-fade-in-scale"
        onClick={(e) => e.stopPropagation()} 
      >
        {/* Optional Close Button */} 
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-salus-azul-escuro/70 hover:text-salus-azul-escuro transition-colors"
          aria-label="Fechar modal"
        >
          <X size={20} />
        </button>

        {/* Title */}
        <h2 className="text-xl font-semibold text-salus-azul-escuro font-heading pr-6">
          {title}
        </h2>
        
        {/* Message */}
        <p className="text-salus-azul-escuro/90">
          {message}
        </p>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-salus-azul-escuro/30 text-salus-azul-escuro hover:bg-salus-azul-escuro/10 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose(); // Optionally close modal after confirm
            }}
            className="px-4 py-2 rounded-lg bg-salus-verde-escuro text-white hover:bg-salus-verde-claro transition-colors"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Add basic fade-in/scale animation (requires Tailwind config extension or just use opacity)
// Example animation - put in your global CSS or index.css if needed
/*
@keyframes fade-in-scale {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in-scale {
  animation: fade-in-scale 0.3s ease-out forwards;
}
*/

export default ConfirmationModal; 